import { BottomNavigation, BottomNavigationAction, Box, Hidden, Icon, Menu, MenuItem, Slide, styled, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useChatPresence } from "../_components/chat/chat.presence";
import { AppSidebar } from "../_components/sidebar/app.sidebar";
import { AppToolbar } from "../_components/toolbar/app.toolbar";
import { ADMIN_NAV, PRIMARY_NAV, USER_NAV } from "../_const/navigation.const";
import { accountService } from "../_services/accounts.service";
import { ChatWindow } from '../_components/chat/chat.window';
import { handleErrorResponse } from "../_helpers/handleResponse.helper";
import { ChatGroupManage } from "../_components/chat/chat.group.manage";
import { chatGroupService } from "../_services/chatGroup.service";
import { useSignalR } from "../_components/chat/chat.signalr.context";

const drawerWidth = 240;

const useStyles = {

    appScreen: {
        display: 'flex',
        backgroundColor: "#e6e6e6",
        height: "100%",
    },
    main: {
        marginTop: "40px",
    },
    root: {

    },
    mainBackground: {},
    stickToBottom: {
        width: '100%',
        position: 'fixed',
        bottom: 0,
        borderTop: '1px solid #e6e6e6',
        zIndex: 9999
    },
    toolbar: {},
};

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
    open?: boolean;
}>(({ theme, open }) => ({
    flexGrow: 1,
    width: 'calc(100% - 500px)',
    maxWidth: "calc(100%)",
    marginLeft: 0,
    [theme.breakpoints.up('md')]: {
        transition: theme.transitions.create('margin', {
            easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
            duration: open
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
        }),
        marginLeft: open ? 0 : `-${drawerWidth}px`,
    },
}));

export const chatWindowBoxStyle = {
    position: "fixed",
    bottom: 0,
    width: 400,
    maxWidth: "100%",
    height: "65%",
    bgcolor: "background.paper",
    boxShadow: 6,
    zIndex: 1400,
    borderLeft: "1px solid #ccc",
    borderTop: "1px solid #ccc",
    left: { xs: 0, md: `${drawerWidth}px` },
};

export function AppScreen() {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

    const [value, setValue] = React.useState(0);
    const [isOpen, setIsOpen] = useState(() =>
        typeof window !== "undefined" && window.matchMedia("(min-width:900px)").matches,
    );
    const [anchorEl, setAnchorEl] = React.useState(null);
    const goTo = useNavigate();
    const [selectedListItem, setSelectedListItem] = useState(null);

    //Chat
    const { connection } = useSignalR();
    const currentUser = accountService.currentUserValue;
    const [recipient, setRecipient] = useState(null);
    const { users } = useChatPresence(currentUser.id);
    const [groups, setGroups] = useState([]);
    const [activeGroup, setActiveGroup] = useState<any>(null);
    const [editingGroup, setEditingGroup] = useState(null);
    const [showManageGroup, setShowManageGroup] = useState(false);
    const [groupUnread, setGroupUnread] = useState<Record<number, number>>({});

    const handleClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (e: any, nav: any) => {
        setAnchorEl(null);
        if (nav && nav.url)
            goTo(nav.url);
    };

    const toggleSidebar = () => {
        setIsOpen(!isOpen);
    };

    const prevMdUpRef = useRef<boolean | undefined>(undefined);
    useEffect(() => {
        if (prevMdUpRef.current === true && isMdUp === false) {
            setIsOpen(false);
        }
        prevMdUpRef.current = isMdUp;
    }, [isMdUp]);

    const handleSelectedItemClick = (item: any) => {
        setSelectedListItem(item);
    };

    const fetchGroups = async () => {
        if (!currentUser || !accountService.isAdmin) return;

        return await chatGroupService.get(currentUser.id)
            .then(
                json => {
                    setGroups(json);
                }, handleErrorResponse)
            .finally(() => { });
    };

    useEffect(() => {
        fetchGroups();
    }, [currentUser]);

    //1:1 chat
    useEffect(() => {
        if (!connection || !currentUser?.id || accountService.isAgent) return;

         const handler = () => {
             fetchGroups();
         };

         connection.on('ChatGroupsChanged', handler);

         return () => {
             connection.off('ChatGroupsChanged', handler);
         };
     }, [connection, currentUser]);

    //Group Chat
     useEffect(() => {
         if (!connection || !groups?.length) return;

         groups.forEach((g: any) => {
             connection.invoke('JoinGroup', `group-${g.id}`)
                 .catch(() => {});
         });
     }, [connection, groups]);

     useEffect(() => {
         if (!connection) return;

         const onGroupMsg = (groupId: number, fromUserId: string, message: string) => {

             if (!activeGroup || activeGroup?.id !== groupId) {

                 if (fromUserId === currentUser?.id) return;
                 setGroupUnread(_msg => ({ ..._msg, [groupId]: (_msg[groupId] || 0) + 1 }));
             }
         };

         connection.on('ReceiveGroupMessage', onGroupMsg);
         return () => { connection.off('ReceiveGroupMessage', onGroupMsg); };
     }, [connection, activeGroup, currentUser]);


    return (
        <div style={useStyles.appScreen} id="app-screen">

            <AppToolbar toggleSidebar={toggleSidebar} />

            <AppSidebar
                isOpen={isOpen}
                variant={isMdUp ? "persistent" : "temporary"}
                onClose={() => setIsOpen(false)}
                onNavigated={() => setIsOpen(false)}
                currentUser={currentUser}
                users={users}
                groups={groups.map((g: any) => ({ ...g, unread: groupUnread[g.id] || 0 }))}
                activeRecipient={recipient}
                activeGroup={activeGroup}
                onSelectRecipient={(user) => { setRecipient(user); setActiveGroup(null); }}
                onSelectGroup={(group) => {
                    setActiveGroup(group);
                    setRecipient(null);
                    setGroupUnread(_msg => ({ ..._msg, [group.id]: 0 }));
                }}
                onGroupManage={(group) => {
                    setEditingGroup(group || null);
                    setShowManageGroup(true);
                }}
            />

            {(recipient || activeGroup) && (
                <Slide direction="up" in={Boolean(recipient || activeGroup)} mountOnEnter unmountOnExit>
                    <Box sx={chatWindowBoxStyle}>
                        <ChatWindow
                            currentUser={currentUser}
                            users={users}
                            recipient={recipient || undefined}
                            group={activeGroup || undefined}
                            onClose={() => {
                                setRecipient(null);
                                setActiveGroup(null);
                            }}
                        />
                    </Box>
                </Slide>
            )}
             
            <Slide direction="up" in={showManageGroup} mountOnEnter unmountOnExit>
                <Box sx={chatWindowBoxStyle}>
                    <ChatGroupManage
                        users={users}
                        currentUser={currentUser}
                        group={editingGroup}
                        onClose={() => setShowManageGroup(false)}
                        onCreate={async (group) => {
                            await chatGroupService.submit(group);
                            await fetchGroups();
                            setShowManageGroup(false);
                        }}
                        onUpdate={async (group) => {
                            await chatGroupService.submit(group);
                            await fetchGroups();
                            setShowManageGroup(false);
                        }}
                        onDelete={async (groupId) => {
                            await chatGroupService.disable(groupId);
                            await fetchGroups();
                            setShowManageGroup(false);
                        }}
                    />
                </Box>
            </Slide> 

            {/*<Main open={isOpen}>*/}
            {/*    <Box sx={{ p: { xs: 1, md: 4 } }}>*/}
            {/*        <Outlet></Outlet>*/}
            {/*    </Box>*/}
            {/*</Main>*/}

            <Main
                open={isOpen}
                sx={{
                    pt: { xs: "56px", md: "64px" },
                    pb: { xs: accountService.isAgent ? 0 : "56px", md: 0 },
                }}
            >
                <Box sx={{ p: { xs: 1, md: 4 } }}>
                    <Outlet />
                </Box>
            </Main>

            {!accountService.isAgent && (
                <Hidden mdUp>
                    <BottomNavigation
                        value={value}
                        onChange={(event, newValue) => {
                            setValue(newValue);
                        }}
                        showLabels={true}
                        sx={{
                            width: '100%',
                            position: 'fixed',
                            bottom: 0,
                            borderTop: '1px solid #e6e6e6',
                        }}>

                        {PRIMARY_NAV && PRIMARY_NAV.map(nav =>
                            <BottomNavigationAction key={"bottomNav_" + nav.name} onClick={() => goTo(nav.url)} label={nav.mobileName || nav.name} icon={
                                <>
                                    {nav.png == null &&
                                        <Icon>{nav.icon}</Icon>
                                    }
                                </>
                            } />
                        )}

                        {accountService.isAdmin &&
                            <BottomNavigationAction label={'Admin'} aria-controls="simple-menu" aria-haspopup="true" onClick={handleClick} icon={
                                <Icon>apps</Icon>
                            } />
                        }

                        {accountService.isAdmin &&
                            <Menu
                                id="simple-menu"
                                anchorEl={anchorEl}
                                keepMounted
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                            >

                                {ADMIN_NAV && ADMIN_NAV.map(nav =>
                                    <MenuItem
                                        sx={selectedListItem === nav ? {
                                            backgroundColor: '#bbc0f6 !important',
                                            color: 'black !important',
                                            borderLeft: '1px solid #fff',
                                        } : {
                                            backgroundColor: 'auto',
                                            color: 'auto',
                                            borderLeft: 'auto',
                                        }}
                                        key={"sideNav_" + nav.name}
                                        onClick={(e: any) => { handleClose(e, nav); handleSelectedItemClick(nav) }}>
                                        {nav.name}
                                    </MenuItem>
                                )}

                            </Menu>
                        }

                        {USER_NAV && USER_NAV.map((nav: any) =>
                            <BottomNavigationAction key={"bottomNav_" + nav.name} onClick={nav.onClick} label={nav.mobileName || nav.name} icon={
                                <>
                                    {nav.png == null &&
                                        <Icon>{nav.icon}</Icon>
                                    }
                                </>
                            } />
                        )}

                    </BottomNavigation>
                </Hidden>
            )}

        </div >
    );
}